import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FilterField } from '@/components/FilterButton'
import { usePersistedState } from '@/hooks/usePersistedState'
import { StatusFilterButton } from '@/components/StatusFilter'
import { deliveryStatusOf } from '@/components/DeliveryStatusBadge'
import { AdvertiserSelect } from '@/components/AdvertiserSelect'
import { ALL_COUNTRIES, CountryFilter } from '@/components/CountryFilter'
import { useNetwork } from '@/contexts/NetworkContext'
import { DeliveryStatusFilter, LineItem } from '@/services/lineItems/lineItemsApi'
import { TeamsApi } from '@/services/users/teamsApi'

// ─── Types ─────────────────────────────────────────────────────────────────

export type ObjectStateFilter  = 'all' | 'ACTIVE' | 'INACTIVE'
export type DeliveryStateFilter = DeliveryStatusFilter

export interface LineItemFilterState {
  objectState:    ObjectStateFilter
  deliveryState:  DeliveryStateFilter
  /** Advertiser id the list is narrowed to, or '' for every advertiser. */
  advertiserId:   string
  /**
   * Country code of the team owning the line item's order, or '' for every
   * country. The API only applies it when no advertiser is picked.
   */
  teamCountry:    string
}

const DEFAULT_FILTERS: LineItemFilterState = {
  objectState:   'ACTIVE',
  deliveryState: 'all',
  advertiserId:  '',
  teamCountry:   '',
}

// ─── Hook ──────────────────────────────────────────────────────────────────

/**
 * @param initial  the page's own starting point — what "unfiltered" means there.
 * @param storageKey  name the list is remembered under; lists sharing a key
 *                    share their filters. Defaults to the main line item list.
 */
export function useLineItemFilters(
  initial?: Partial<LineItemFilterState>,
  storageKey = 'lineItems',
) {
  // The page's own starting point is what "unfiltered" means for that page,
  // so an embedded list starting on 'all' does not look filtered.
  const [defaults] = useState<LineItemFilterState>({ ...DEFAULT_FILTERS, ...initial })
  const [stored, setFilters] = usePersistedState<LineItemFilterState>(`filters.${storageKey}`, defaults)

  // A remembered value predates any filter added since it was stored, so the
  // defaults fill in what it does not carry.
  const filters: LineItemFilterState = { ...defaults, ...stored }

  function applyFilters(items: LineItem[]): LineItem[] {
    return items.filter((li) => {
      if (filters.objectState  !== 'all' && li.objectState  !== filters.objectState)  return false
      if (filters.deliveryState !== 'all' && deliveryStatusOf(li) !== filters.deliveryState) return false
      if (filters.advertiserId && li.advertiserId !== filters.advertiserId) return false
      return true
    })
  }

  return { filters, setFilters, applyFilters, defaults }
}

// ─── Filter button ─────────────────────────────────────────────────────────

/** The delivery statuses the API can filter on, in the order they are offered. */
const DELIVERY_OPTIONS: DeliveryStateFilter[] = [
  'all', 'RUNNING', 'READY', 'PAUSED', 'NOT_READY', 'PROPOSED', 'STOPPED', 'ENDED',
]

interface LineItemFiltersProps {
  filters: LineItemFilterState
  onChange: (f: LineItemFilterState) => void
  /** What the page treats as unfiltered — from `useLineItemFilters`. */
  defaults: LineItemFilterState
  /** Off for lists already scoped to one advertiser, where it says nothing. */
  showAdvertiser?: boolean
  /** On for lists fetched across advertisers, where the team country applies. */
  showCountry?: boolean
}

/** Status and delivery state filters for a line item list, in one button. */
export function LineItemFilters({
  filters,
  onChange,
  defaults,
  showAdvertiser = true,
  showCountry = false,
}: LineItemFiltersProps) {
  const { t } = useTranslation()
  const { scopeKey } = useNetwork()

  // The countries on offer are those the network's teams are in.
  const { data: teams = [] } = useQuery({
    queryKey: ['teams', 'lineItemFilter', scopeKey],
    queryFn: () => TeamsApi.getAll(),
    enabled: showCountry,
    staleTime: 5 * 60 * 1000,
  })
  const countryCodes = useMemo(
    () => [...new Set(teams.map((team) => team.country).filter((c): c is string => !!c))],
    [teams]
  )
  // One country has nothing to narrow, but a remembered pick stays visible so
  // it can be cleared.
  const countryVisible = showCountry && (countryCodes.length > 1 || !!filters.teamCountry)

  const label = (value: DeliveryStateFilter) =>
    value === 'all' ? t('common.all') : t(`deliveryStatuses.${value}`)

  return (
    <StatusFilterButton
      value={filters.objectState}
      onChange={(v) => onChange({ ...filters, objectState: v })}
      defaultValue={defaults.objectState}
      extraActiveCount={
        (filters.deliveryState !== defaults.deliveryState ? 1 : 0) +
        (showAdvertiser && filters.advertiserId !== defaults.advertiserId ? 1 : 0) +
        (countryVisible && filters.teamCountry !== defaults.teamCountry ? 1 : 0)
      }
      onReset={() => onChange(defaults)}
    >
      <FilterField label={t('common.delivery')}>
        <Select
          value={filters.deliveryState}
          onValueChange={(v) => onChange({ ...filters, deliveryState: v as DeliveryStateFilter })}
        >
          <SelectTrigger className="h-8 w-full text-sm">
            <SelectValue>{label(filters.deliveryState)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {DELIVERY_OPTIONS.map((value) => (
              <SelectItem key={value} value={value}>{label(value)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      {showAdvertiser && (
        <FilterField label={t('common.advertiser')}>
          <AdvertiserSelect
            value={filters.advertiserId}
            // The API cannot narrow by advertiser and team country at once.
            onChange={(advertiserId) =>
              onChange({ ...filters, advertiserId, teamCountry: advertiserId ? '' : filters.teamCountry })
            }
            anyLabel={t('common.all')}
          />
        </FilterField>
      )}

      {countryVisible && (
        <FilterField label={t('common.country')}>
          <CountryFilter
            value={filters.teamCountry || ALL_COUNTRIES}
            onChange={(v) => {
              const teamCountry = v === ALL_COUNTRIES ? '' : v
              onChange({ ...filters, teamCountry, advertiserId: teamCountry ? '' : filters.advertiserId })
            }}
            codes={countryCodes}
          />
        </FilterField>
      )}
    </StatusFilterButton>
  )
}
